import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import AppActionMenu from './AppActionMenu'
import ReportColumnsModal from './ReportColumnsModal'
import ReportScheduleModal from './ReportScheduleModal'
import { appToast } from './AppToast'
import {
  fetchPredefinedReports,
  runPredefinedReport,
  exportPredefinedReport,
  fetchReportPreferences,
  saveReportPreferences,
} from '../features/reports/api/reportsApi'
import { executePrintReport } from '../utils/printReportUtil'

export const LECTURE_PREDEFINED_REPORTS = [
  {
    reportKey: 'teacher_wise_daily_schedule',
    title: 'Teacher Wise Daily Lecture Schedule',
    description: 'Shows the list of Scheduled Lectures across teachers and subjects.',
    defaultColumns: ['From Time', 'Till Time', 'Min', 'Branch', 'Course', 'Batch', 'Subject', 'Topic', 'AYear'],
    availableColumns: ['From Time', 'Till Time', 'Min', 'Institute Name', 'Branch', 'Course', 'Batch', 'Date', 'From', 'Till', 'Subject', 'Chapter Name', 'Faculty Name', 'Topic', 'AYear'],
  },
  {
    reportKey: 'teacher_wise_lecture',
    title: 'Teacher Wise Lecture',
    description: 'Shows the chronological lecture logs and delivered sessions teacher wise.',
    defaultColumns: ['Date', 'From Time', 'Till Time', 'Min', 'Branch', 'Course', 'Batch', 'Subject', 'AYear'],
    availableColumns: ['Date', 'From Time', 'Till Time', 'Min', 'Institute Name', 'Branch', 'Course', 'Batch', 'From', 'Till', 'Subject', 'Chapter Name', 'Faculty Name', 'AYear'],
  },
  {
    reportKey: 'topic_wise_lecture',
    title: 'Topic Wise Lecture',
    description: 'Shows the aggregated lecture counts and teaching hours topic and chapter wise.',
    defaultColumns: ['Institute Name', 'Branch Name', 'Course', 'Batch', 'Date', 'aFrom', 'aTill', 'Subject', 'Topic', 'Lectures', 'Chapter Name', 'Faculty Name', 'AYear', 'Hours'],
  },
  {
    reportKey: 'course_wise_timetable',
    title: 'Course Wise Lectures (Time-Table)',
    description: 'List of scheduled lectures and syllabus timetable for each course and branch.',
    defaultColumns: ['Batch', 'Date', 'From', 'Till', 'Subject', 'Topic', 'Faculty'],
    availableColumns: ['Institute Name', 'Branch Name', 'Course Name', 'Batch', 'Date', 'From', 'Till', 'Subject', 'Topic', 'Chapter Name', 'Faculty'],
  },
  {
    reportKey: 'faculty_wise_timetable',
    title: 'Faculty-Wise Lectures (Time-Table)',
    description: 'Weekly schedule and timetable allocation for each faculty member.',
    defaultColumns: ['Faculty', 'Day', 'Date', 'From', 'Till', 'Course', 'Batch', 'Subject', 'Topic', 'Branch', 'Institute Name'],
  },
  {
    reportKey: 'faculty_exception_report',
    title: 'Faculty Exception Report',
    description: 'Faculty-wise lectures that were cancelled, started late, or ended early.',
    defaultColumns: ['Institute Name', 'Date', 'Branch', 'Course', 'Batch', 'Subject', 'SL', 'LE', 'C', 'Faculty Name'],
  },
  {
    reportKey: 'lecture_wise_payment',
    title: 'Lecture Wise Payment',
    description: 'Calculates hourly payment and total honorarium per lecture for visiting and permanent faculty.',
    defaultColumns: ['Institute Name', 'Faculty Name', 'Date', 'Time', 'Branch', 'Course', 'Batch', 'Subject', 'Topic', 'Rate', 'Duration', 'Amount'],
  },
  {
    reportKey: 'lecture_attendance',
    title: 'Lecture Attendance',
    description: 'Lecture-wise monthly attendance percentage, presence, and absence breakdown.',
    defaultColumns: ['Roll No', 'Name', 'Lecture', 'Present', 'Absent', 'Percentage', 'Branch', 'Course', 'Batch'],
  },
  {
    reportKey: 'lecture_hours_summary',
    title: 'Lecture Hours & Faculty Teaching Ledger',
    description: 'Faculty-wise lecture hours delivery summary with topic details and total duration.',
    defaultColumns: ['Date', 'Faculty Name', 'Subject', 'Topic', 'Course', 'Batch', 'Branch', 'From', 'Till', 'Duration'],
  },
]

export const ATTENDANCE_PREDEFINED_REPORTS = [
  {
    reportKey: 'daily_attendance_register',
    title: 'Daily Student Attendance Register',
    description: 'Detailed daily attendance register with student status, timestamps, and absence remarks.',
    defaultColumns: ['Roll No', 'Student Name', 'Course', 'Batch', 'Attendance Status', 'Biometric Swipes', 'Reason / Remarks'],
  },
  {
    reportKey: 'absentees_defaulters_summary',
    title: 'Absentees & Defaulters Summary',
    description: 'Summary of absent students with parent contact details and recorded absence reasons.',
    defaultColumns: ['Roll No', 'Student Name', 'Course', 'Batch', 'Parent Mobile', 'Absence Reason'],
  },
  {
    reportKey: 'biometric_swipe_variance',
    title: 'Biometric Swipe Reconciliation',
    description: 'Comparison of scheduled session timings versus machine swipe logs and grace limits.',
    defaultColumns: ['Student Name', 'Card No', 'Scheduled Time', 'First In-Swipe', 'Last Out-Swipe', 'Variance'],
  },
  {
    reportKey: 'monthly_attendance_matrix',
    title: 'Monthly Attendance Compliance',
    description: 'Consolidated month-wise student attendance percentages and minimum threshold compliance.',
    defaultColumns: ['Roll No', 'Student Name', 'Total Working Days', 'Present Days', 'Absent Days', 'Attendance %', 'Compliance Status'],
  },
]

export const REMARKS_PREDEFINED_REPORTS = [
  {
    reportKey: 'student_remarks_register',
    title: 'Student Remarks Register',
    description: 'Comprehensive chronological register of student behavioral, disciplinary, and achievement remarks.',
    defaultColumns: ['Date', 'Roll No', 'Student Name', 'Category', 'Course', 'Batch', 'Particulars', 'Remarked By'],
  },
  {
    reportKey: 'remarks_category_distribution',
    title: 'Remark Category Breakdown',
    description: 'Statistical distribution of remarks categorized by positive appreciation versus disciplinary warnings.',
    defaultColumns: ['Remark Category', 'Total Count', 'Percentage', 'Top Course', 'Last Recorded'],
  },
  {
    reportKey: 'disciplinary_action_summary',
    title: 'Disciplinary & Warning Summary',
    description: 'Students flagged with multiple warnings or repeated infractions requiring counseling / parent intervention.',
    defaultColumns: ['Roll No', 'Student Name', 'Warnings Count', 'Recent Infraction', 'Parent Contacted'],
  },
  {
    reportKey: 'faculty_remarks_activity',
    title: 'Faculty Observations Log',
    description: 'Summary of remarks and behavioral notes submitted per teacher and department.',
    defaultColumns: ['Faculty Name', 'Department', 'Total Remarks Logged', 'Last Active Date'],
  },
]

export const NOTICE_PREDEFINED_REPORTS = [
  {
    reportKey: 'notice_broadcast_register',
    title: 'Notice Broadcast Register',
    description: 'Chronological archive of all published institutional notices, circulars, and announcements.',
    defaultColumns: ['Date', 'Headline', 'Category', 'Target Audience', 'Status', 'Posted By'],
  },
  {
    reportKey: 'audience_delivery_breakdown',
    title: 'Audience Delivery Breakdown',
    description: 'Analysis of notice distribution across student courses, batches, and parent mobile apps.',
    defaultColumns: ['Target Audience', 'Total Notices', 'Active Notices', 'Last Broadcast Date'],
  },
  {
    reportKey: 'notice_category_distribution',
    title: 'Category Distribution Report',
    description: 'Statistical breakdown of circulars by type (Academic, Holidays, Fee Reminders, Emergency).',
    defaultColumns: ['Notice Category', 'Total Count', 'Percentage', 'Active Now'],
  },
]

export const TEST_PREDEFINED_REPORTS = [
  {
    reportKey: '1163',
    title: 'Test Marks (Single Test)',
    description: 'Detailed student-wise marks statement for a single examination test.',
    defaultColumns: ['Roll No', 'Student Name', 'Marks', 'Max Marks', 'Percentage', 'Rank'],
  },
  {
    reportKey: '2163',
    title: 'Test Marks Consolidated',
    description: 'Consolidated aggregate scores, percentages, and rankings across multiple tests.',
    defaultColumns: ['Roll No', 'Student Name', 'Total Marks', 'Average %', 'Tests Attempted', 'Overall Rank'],
  },
  {
    reportKey: '2164',
    title: 'Student-wise Test Performance',
    description: 'Individual student longitudinal progress tracking across all attempted examinations.',
    defaultColumns: ['Test No', 'Date', 'Topic', 'Subject', 'Marks', 'Max Marks', '% Scored', 'Class Highest'],
  },
  {
    reportKey: '3164',
    title: 'Test Marks Grid (Subject Matrix)',
    description: 'Cross-tabulated student score matrix across all examination subjects.',
    defaultColumns: ['Roll No', 'Student Name', 'Subject 1', 'Subject 2', 'Total Marks', 'Rank'],
  },
  {
    reportKey: '3165',
    title: 'Test Absentee Roster',
    description: 'List of all candidates absent from scheduled tests with parent contact details.',
    defaultColumns: ['Roll No', 'Student Name', 'Branch', 'Batch', 'Contact No', 'Parent Mobile'],
  },
  {
    reportKey: '3166',
    title: 'Test Topic-wise Syllabus Analysis',
    description: 'Curriculum coverage analysis and average student score breakdown by test topic.',
    defaultColumns: ['Topic', 'Questions', 'Total Marks', 'Average Score', 'Pass %'],
  },
  {
    reportKey: '3167',
    title: 'Comparative Batch Performance',
    description: 'Batch-to-batch comparative performance metrics, averages, and evaluation graphs.',
    defaultColumns: ['Batch', 'Appeared', 'Absent', 'Average %', 'Highest Score', 'Lowest Score'],
  },
]

export default function PredefinedReportsDrawer({
  isOpen = false,
  onClose,
  module = 'education',
  submodule = null,
  moduleLabel = 'Lectures',
  title = 'Predefined Reports',
  subtitle,
  filters = {},
  reports: propReports,
  selectedReportKey: controlledSelectedKey,
  onSelectReport,
  onAction,
  isLoading: propIsLoading = false,
  reportResult = null,
}) {
  const effectiveModule = (module || (moduleLabel === 'Lectures' ? 'education' : moduleLabel) || 'education').toLowerCase()

  const [dynamicReports, setDynamicReports] = useState([])
  const [isDynamicLoading, setIsDynamicLoading] = useState(false)
  const [internalSelectedKey, setInternalSelectedKey] = useState(null)
  const [settingReport, setSettingReport] = useState(null)
  const [settingSavedCols, setSettingSavedCols] = useState(null)
  const [scheduleReport, setScheduleReport] = useState(null)

  useEffect(() => {
    if (!isOpen) return

    // If caller provided static/custom reports, use them
    if (Array.isArray(propReports) && propReports.length > 0) {
      return
    }

    let isMounted = true
    setIsDynamicLoading(true)
    fetchPredefinedReports(effectiveModule, submodule)
      .then((items) => {
        if (isMounted && Array.isArray(items) && items.length > 0) {
          setDynamicReports(items)
        }
      })
      .catch((err) => {
        console.warn('[PredefinedReportsDrawer] Could not fetch dynamic reports, using static fallback:', err)
      })
      .finally(() => {
        if (isMounted) setIsDynamicLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [isOpen, effectiveModule, submodule, propReports])

  const staticFallback =
    effectiveModule === 'attendance'
      ? ATTENDANCE_PREDEFINED_REPORTS
      : effectiveModule === 'remarks'
      ? REMARKS_PREDEFINED_REPORTS
      : effectiveModule === 'notice'
      ? NOTICE_PREDEFINED_REPORTS
      : submodule === 'tests' || effectiveModule === 'tests'
      ? TEST_PREDEFINED_REPORTS
      : LECTURE_PREDEFINED_REPORTS

  const activeReports =
    (Array.isArray(propReports) && propReports.length > 0)
      ? propReports
      : (dynamicReports.length > 0 ? dynamicReports : staticFallback)

  const activeKey = controlledSelectedKey !== undefined ? controlledSelectedKey : internalSelectedKey
  const isLoading = propIsLoading || isDynamicLoading

  if (!isOpen) return null

  const handleSelect = (key) => {
    setInternalSelectedKey(key)
    onSelectReport?.(key)
  }

  const handleAction = async (actionKey, reportKey) => {
    setInternalSelectedKey(reportKey)
    const report = activeReports.find((r) => (r.reportKey || r.id) === reportKey) || { reportKey, title: reportKey }

    if (actionKey === 'setting') {
      if (onAction) {
        onAction(actionKey, reportKey, report)
        return
      }
      try {
        const pref = await fetchReportPreferences(effectiveModule, reportKey)
        if (pref?.columns && pref.columns.length > 0) {
          setSettingSavedCols(pref.columns)
        } else {
          setSettingSavedCols(null)
        }
      } catch {
        setSettingSavedCols(null)
      }
      setSettingReport(report)
      return
    }

    if (actionKey === 'schedule') {
      const schedulePayload = {
        ...report,
        module: report?.module || report?.targetModule || effectiveModule,
        targetModule: report?.module || report?.targetModule || effectiveModule,
        filters,
      }
      if (onAction) {
        onAction(actionKey, reportKey, schedulePayload)
        return
      }
      setScheduleReport(schedulePayload)
      return
    }

    if (onAction) {
      onAction(actionKey, reportKey)
      return
    }

    const reportTitle = report ? report.title : reportKey

    if (actionKey === 'print') {
      try {
        appToast.info(`Generating print preview for: ${reportTitle}...`)
        const res = await runPredefinedReport({
          module: effectiveModule,
          reportKey,
          filters: filters || {},
        })
        const printCols = res?.table?.columns || (report.columns && report.columns.map((c) => ({ key: c.key || c.name || c, label: c.label || c }))) || []
        const printRows = res?.table?.rows || res?.rows || []
        const totalRow = res?.table?.totalRow || null

        await executePrintReport({
          title: reportTitle,
          subtitle: subtitle || `${moduleLabel || effectiveModule} Predefined Report`,
          metadata: {
            Module: moduleLabel || effectiveModule,
            'Generated At': new Date().toLocaleString(),
          },
          columns: printCols,
          totalRow,
          fetchReportData: async () => printRows,
          onExportExcel: () => handleAction('excel', reportKey),
          onExportPdf: () => handleAction('pdf', reportKey),
        })
      } catch (err) {
        console.error('Failed to run print report', err)
        appToast.error(err?.response?.data?.detail || err?.message || 'Failed to generate print report.')
      }
    } else if (actionKey === 'excel' || actionKey === 'pdf') {
      const isPdf = actionKey === 'pdf'
      const format = isPdf ? 'pdf' : 'xlsx'
      try {
        appToast.info(`Exporting ${isPdf ? 'PDF' : 'Excel'} data for: ${reportTitle}...`)
        const { blob, filename } = await exportPredefinedReport({
          module: effectiveModule,
          reportKey,
          format,
          filters: filters || {},
        })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename || `${reportKey}.${format}`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        appToast.success(`${isPdf ? 'PDF' : 'Excel'} downloaded successfully.`)
      } catch (err) {
        console.error(`Failed to export ${format} report`, err)
        appToast.error(err?.response?.data?.detail || err?.message || `Failed to export ${format.toUpperCase()}.`)
      }
    }
  }

  const drawerContent = (
    <>
      <div className="fixed inset-0 z-[300] pointer-events-none overflow-hidden">
        {/* Click outside backdrop */}
        <button
          type="button"
          className="absolute inset-0 bg-on-surface/20 backdrop-blur-sm pointer-events-auto cursor-pointer border-none"
          onClick={onClose}
          aria-label="Close reports drawer"
        />

        {/* Drawer Panel */}
        <div className="absolute right-0 top-0 bottom-0 w-[520px] max-w-full bg-white shadow-2xl pointer-events-auto flex flex-col border-l border-outline-variant/30 overflow-hidden">
          
          {/* Header */}
          <div className="p-8 border-b border-surface-container flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-extrabold text-primary font-headline">{title}</h3>
              <p className="text-sm text-on-surface-variant">
                {subtitle || `${moduleLabel} Analytics Module`}
              </p>
            </div>
            <button
              type="button"
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-surface-container transition-colors cursor-pointer"
              onClick={onClose}
              aria-label="Close drawer"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          {/* Reports List */}
          <div className="flex-1 overflow-y-auto overscroll-contain p-8 space-y-4">
            {isLoading && (
              <p className="text-sm text-on-surface-variant">Loading predefined reports...</p>
            )}

            {activeReports.map((report) => {
              const key = report.reportKey || report.id
              const isSelected = activeKey === key
              const colCount = report.defaultColumns?.length || report.columnsCount || 0

              const rowMatchedText =
                reportResult?.reportKey === key && reportResult?.summary
                  ? `${reportResult.summary.totalRows || 0} rows matched`
                  : report.rowsMatched || ''

              return (
                <div
                  key={key}
                  className={
                    isSelected
                      ? 'bg-surface-container-lowest border border-primary/20 rounded-xl p-5 relative cursor-pointer shadow-xs'
                      : 'bg-surface-container-low hover:bg-surface-container rounded-xl p-5 transition-colors group cursor-pointer'
                  }
                  onClick={() => handleSelect(key)}
                >
                  <div className="flex justify-between items-start mb-2 gap-3">
                    <div>
                      <h4 className="font-bold text-on-surface">{report.title}</h4>
                      <p className="text-xs text-on-surface-variant">
                        {colCount} columns configured
                      </p>
                    </div>
                    <div
                      className="shrink-0"
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <AppActionMenu
                        triggerIcon={null}
                        triggerLabel="Print Report"
                        triggerClassName={
                          isSelected
                            ? 'bg-primary text-on-primary font-semibold text-xs rounded-lg px-3 py-1.5 shadow-xs'
                            : 'bg-surface-container-highest text-on-surface font-semibold text-xs rounded-lg px-3 py-1.5'
                        }
                        items={[
                          { key: 'print', label: 'Print', icon: 'print' },
                          { key: 'excel', label: 'Excel', icon: 'description' },
                          { key: 'pdf', label: 'PDF', icon: 'picture_as_pdf' },
                          { key: 'schedule', label: 'Schedule Report', icon: 'event' },
                          { key: 'setting', label: 'Setting', icon: 'settings' },
                        ]}
                        onSelect={(actionKey) => handleAction(actionKey, key)}
                      />
                    </div>
                  </div>

                  <p className="text-sm text-on-surface-variant mb-2 leading-relaxed">
                    {report.description || 'No description provided.'}
                  </p>

                  {rowMatchedText ? (
                    <p className="text-[11px] text-primary font-semibold">
                      {rowMatchedText}
                    </p>
                  ) : null}
                </div>
              )
            })}
          </div>

        </div>
      </div>

      {/* ── Report Columns Settings Modal ── */}
      <ReportColumnsModal
        open={Boolean(settingReport)}
        report={settingReport}
        onClose={() => setSettingReport(null)}
        onSave={async (newCols) => {
          const repKey = settingReport?.reportKey || settingReport?.id
          if (repKey) {
            try {
              await saveReportPreferences({
                module: effectiveModule,
                reportKey: repKey,
                columns: newCols,
              })
              appToast.success(`Column preferences saved for ${settingReport.title}`)
            } catch (err) {
              console.warn('[PredefinedReportsDrawer] Could not save preference to backend:', err)
            }
            try {
              localStorage.setItem(`report_cols_${repKey}`, JSON.stringify(newCols))
            } catch {}
          }
          setSettingReport(null)
        }}
        savedColumns={
          settingSavedCols ||
          ((settingReport?.reportKey || settingReport?.id)
            ? JSON.parse(localStorage.getItem(`report_cols_${settingReport.reportKey || settingReport.id}`) || 'null')
            : null)
        }
      />

      {/* ── Report Schedule Modal ── */}
      <ReportScheduleModal
        open={Boolean(scheduleReport)}
        report={scheduleReport}
        module={effectiveModule}
        moduleLabel={moduleLabel}
        onClose={() => setScheduleReport(null)}
      />
    </>
  )

  return createPortal(drawerContent, document.body)
}
